import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, Calendar, Coins, Tag, FileText, Info, AlertCircle, CheckCircle } from "lucide-react";
import { useAuthStore } from "@/shared/hooks/use-auth-store";
import { getBrowserSupabaseClient } from "@/lib/supabase/client";
import { useToast } from "@/client/components/common/toast";
import styles from "./task-create-page.module.css";

// PRD 定义的6个分类
const categories = [
  { value: "本地化部署", label: "本地化部署" },
  { value: "AI模型", label: "AI模型" },
  { value: "Agent开发", label: "Agent开发" },
  { value: "Web3", label: "Web3" },
  { value: "数据分析", label: "数据分析" },
  { value: "其他", label: "其他" },
] as const;

const categoryTagSuggestions: Record<string, string[]> = {
  "本地化部署": ["Docker", "Kubernetes", "Linux", "GPU", "CUDA"],
  "AI模型": ["Python", "PyTorch", "TensorFlow", "HuggingFace", "GPT"],
  "Agent开发": ["LangChain", "AutoGen", "CrewAI", "React", "Node.js"],
  "Web3": ["Solidity", "Ethers.js", "Rust", "Hardhat", "DeFi"],
  "数据分析": ["Python", "Pandas", "SQL", "Tableau", "Spark"],
  "其他": ["React", "Vue", "TypeScript", "Go", "Java"],
};

// 技术标签输入组件
function TagInput({
  value,
  onChange,
  suggestions,
  maxTags = 5,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  maxTags?: number;
}) {
  const [inputVal, setInputVal] = useState("");

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || value.length >= maxTags || value.includes(trimmed)) return;
    onChange([...value, trimmed]);
    setInputVal("");
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputVal);
    }
    if (e.key === "Backspace" && !inputVal && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const filteredSuggestions = suggestions.filter(
    (s) => s.toLowerCase().includes(inputVal.toLowerCase()) && !value.includes(s)
  );

  return (
    <div className={styles.tagInputWrapper}>
      <div className={styles.tagInputContainer}>
        {value.map((tag) => (
          <span key={tag} className={styles.tagChip}>
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className={styles.tagRemove}>
              ×
            </button>
          </span>
        ))}
        {value.length < maxTags && (
          <input
            type="text"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? "输入标签按回车添加" : ""}
            className={styles.tagInput}
          />
        )}
      </div>
      {filteredSuggestions.length > 0 && inputVal && (
        <div className={styles.suggestions}>
          {filteredSuggestions.slice(0, 5).map((s) => (
            <button key={s} type="button" onClick={() => addTag(s)} className={styles.suggestion}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// 表单步骤
const steps = [
  { key: "basic", label: "基本信息" },
  { key: "detail", label: "任务详情" },
  { key: "confirm", label: "确认发布" },
];

export default function TaskCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const toast = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 表单数据
  const [formData, setFormData] = useState({
    title: "",
    category: "" as typeof categories[number]["value"] | "",
    techTags: [] as string[],
    rewardUsdc: "",
    biddingDeadline: "",      // 直接选择日期
    completionDeadline: "",   // 直接选择日期
    description: "",
    deliveryStandard: "",
  });

  // 验证当前步骤
  const validateStep = useCallback((step: number): boolean => {
    switch (step) {
      case 0:
        if (!formData.title.trim() || formData.title.length < 5) {
          toast.error("标题至少需要5个字符");
          return false;
        }
        if (!formData.category) {
          toast.error("请选择任务分类");
          return false;
        }
        if (formData.techTags.length === 0) {
          toast.error("请至少添加一个技术标签");
          return false;
        }
        return true;
      case 1:
        if (!formData.rewardUsdc || Number(formData.rewardUsdc) < 100) {
          toast.error("赏金金额至少为100 USDC");
          return false;
        }
        if (!formData.biddingDeadline) {
          toast.error("请选择投递截止日期");
          return false;
        }
        const biddingDate = new Date(formData.biddingDeadline);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (biddingDate < now) {
          toast.error("投递截止日期不能早于今天");
          return false;
        }
        if (!formData.completionDeadline) {
          toast.error("请选择项目交付日期");
          return false;
        }
        const completionDate = new Date(formData.completionDeadline);
        if (completionDate <= biddingDate) {
          toast.error("项目交付日期必须晚于投递截止日期");
          return false;
        }
        if (!formData.description.trim() || formData.description.length < 50) {
          toast.error("任务描述至少需要50个字符");
          return false;
        }
        if (!formData.deliveryStandard.trim()) {
          toast.error("请填写交付标准");
          return false;
        }
        return true;
      default:
        return true;
    }
  }, [formData, toast]);

  // 下一步
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  // 上一步
  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!user) {
      toast.error("请先登录");
      return;
    }

    setIsSubmitting(true);
    try {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) {
        throw new Error("Supabase 未配置");
      }

      // 格式化截止日期（添加当天结束时间 23:59:59）
      const biddingDeadline = new Date(formData.biddingDeadline);
      biddingDeadline.setHours(23, 59, 59, 999);

      const completionDeadline = new Date(formData.completionDeadline);
      completionDeadline.setHours(23, 59, 59, 999);

      // 创建任务
      const { data, error } = await supabase
        .from("bounties")
        .insert({
          publisher_id: user.id,
          title: formData.title.trim(),
          description: formData.description.trim(),
          category: formData.category,
          tech_tags: formData.techTags,
          reward_usdc: Number(Number(formData.rewardUsdc).toFixed(2)),
          bidding_deadline: biddingDeadline.toISOString(),
          completion_deadline: completionDeadline.toISOString(),
          delivery_standard: formData.deliveryStandard.trim(),
          status: "drafting", // 先创建为草稿
          bid_count: 0,
          review_round: 0,
          platform_fee_rate: 0.1000,
        })
        .select("id")
        .single();

      if (error) {
        throw new Error(error.message);
      }

      // 调用 RPC 发布任务（冻结资金并切换到 bidding 状态）
      const publishResult = await supabase.rpc("publish_bounty", {
        p_bounty_id: data.id,
        p_publisher_id: user.id,
      });

      // RPC 返回的是 JSONB 对象，不是 error
      if (publishResult.error) {
        // RPC 执行错误，删除草稿
        await supabase.from("bounties").delete().eq("id", data.id);
        throw new Error(publishResult.error.message);
      }

      // 检查业务逻辑返回的 success 字段
      const resultData = publishResult.data as { success: boolean; escrow_id?: string; error?: string } | null;
      if (!resultData?.success) {
        // 业务逻辑失败，删除草稿
        await supabase.from("bounties").delete().eq("id", data.id);
        throw new Error(resultData?.error || "发布失败，请重试");
      }

      toast.success("任务发布成功！");
      navigate("/tasks");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "发布失败，请重试");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 更新表单字段
  const updateField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // 步骤内容渲染
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className={styles.stepContent}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FileText className={styles.labelIcon} />
                任务标题 <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="简洁明了地描述任务，例如：开发一个React组件库"
                className={styles.input}
                maxLength={100}
              />
              <span className={styles.hint}>{formData.title.length}/100</span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <Tag className={styles.labelIcon} />
                任务分类 <span className={styles.required}>*</span>
              </label>
              <div className={styles.categoryGrid}>
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => updateField("category", cat.value)}
                    className={`${styles.categoryBtn} ${formData.category === cat.value ? styles.active : ""}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <Tag className={styles.labelIcon} />
                技术标签 <span className={styles.required}>*</span>
                <span className={styles.hintInline}>（最多5个）</span>
              </label>
              <TagInput
                value={formData.techTags}
                onChange={(tags) => updateField("techTags", tags)}
                suggestions={formData.category ? categoryTagSuggestions[formData.category] : []}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className={styles.stepContent}>
            <div className={styles.formRow}>
              <div className={`${styles.formGroup} ${styles.half}`}>
                <label className={styles.label}>
                  <Coins className={styles.labelIcon} />
                  赏金金额 <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWithSuffix}>
                  <input
                    type="number"
                    value={formData.rewardUsdc}
                    onChange={(e) => updateField("rewardUsdc", e.target.value)}
                    placeholder="1000"
                    className={styles.input}
                    min={100}
                  />
                  <span className={styles.suffix}>USDC</span>
                </div>
                <span className={styles.hint}>最低100 USDC，平台将收取10%手续费</span>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={`${styles.formGroup} ${styles.half}`}>
                <label className={styles.label}>
                  <Calendar className={styles.labelIcon} />
                  投递截止日期 <span className={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.biddingDeadline}
                  onChange={(e) => updateField("biddingDeadline", e.target.value)}
                  className={styles.input}
                  min={new Date().toISOString().split('T')[0]}
                />
                <span className={styles.hint}>工程师可以投递方案的最后日期</span>
              </div>

              <div className={`${styles.formGroup} ${styles.half}`}>
                <label className={styles.label}>
                  <Calendar className={styles.labelIcon} />
                  项目交付日期 <span className={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={formData.completionDeadline}
                  onChange={(e) => updateField("completionDeadline", e.target.value)}
                  className={styles.input}
                  min={formData.biddingDeadline || new Date().toISOString().split('T')[0]}
                />
                <span className={styles.hint}>项目最终必须完成的日期</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <Info className={styles.labelIcon} />
                任务描述 <span className={styles.required}>*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="详细描述任务背景、目标、具体需求..."
                className={styles.textarea}
                rows={6}
                maxLength={5000}
              />
              <span className={styles.hint}>{formData.description.length}/5000</span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <CheckCircle className={styles.labelIcon} />
                交付标准 <span className={styles.required}>*</span>
              </label>
              <textarea
                value={formData.deliveryStandard}
                onChange={(e) => updateField("deliveryStandard", e.target.value)}
                placeholder="明确验收标准，例如：代码质量要求、文档要求、测试要求等..."
                className={styles.textarea}
                rows={4}
                maxLength={1000}
              />
              <span className={styles.hint}>{formData.deliveryStandard.length}/1000</span>
            </div>
          </div>
        );

      case 2:
        const totalCost = Number(formData.rewardUsdc) * 1.1; // 包含10%平台费

        // 格式化日期显示
        const formatDateCN = (dateStr: string) => {
          const date = new Date(dateStr);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const weekDay = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][date.getDay()];
          return `${year}-${month}-${day} ${weekDay}`;
        };

        // 计算天数
        const calcDaysBetween = (start: string, end: string) => {
          const startDate = new Date(start);
          const endDate = new Date(end);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          const diffTime = endDate.getTime() - startDate.getTime();
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        };

        const today = new Date().toISOString().split("T")[0];
        const biddingDays = calcDaysBetween(today, formData.biddingDeadline);
        const totalDays = calcDaysBetween(today, formData.completionDeadline);
        const deliverDays = calcDaysBetween(formData.biddingDeadline, formData.completionDeadline);

        return (
          <div className={styles.stepContent}>
            <div className={styles.confirmCard}>
              <h3 className={styles.confirmTitle}>发布确认</h3>

              <div className={styles.confirmItem}>
                <span className={styles.confirmLabel}>任务标题</span>
                <span className={styles.confirmValue}>{formData.title}</span>
              </div>

              <div className={styles.confirmItem}>
                <span className={styles.confirmLabel}>分类</span>
                <span className={styles.confirmValue}>{formData.category}</span>
              </div>

              <div className={styles.confirmItem}>
                <span className={styles.confirmLabel}>技术标签</span>
                <span className={styles.confirmValue}>{formData.techTags.join(", ")}</span>
              </div>

              <div className={styles.confirmItem}>
                <span className={styles.confirmLabel}>赏金金额</span>
                <span className={styles.confirmValue}>{formData.rewardUsdc} USDC</span>
              </div>

              <div className={styles.confirmItem}>
                <span className={styles.confirmLabel}>平台手续费 (10%)</span>
                <span className={styles.confirmValue}>{(Number(formData.rewardUsdc) * 0.1).toFixed(2)} USDC</span>
              </div>

              <div className={`${styles.confirmItem} ${styles.highlight}`}>
                <span className={styles.confirmLabel}>总支付金额</span>
                <span className={styles.confirmValueHighlight}>{totalCost.toFixed(2)} USDC</span>
              </div>

              {/* 任务描述 */}
              <div className={`${styles.confirmItem} ${styles.fullWidth}`}>
                <span className={styles.confirmLabel}>任务描述</span>
                <div className={styles.confirmDescription}>{formData.description}</div>
              </div>

              {/* 交付标准 */}
              <div className={`${styles.confirmItem} ${styles.fullWidth}`}>
                <span className={styles.confirmLabel}>交付标准</span>
                <div className={styles.confirmDescription}>{formData.deliveryStandard}</div>
              </div>

              {/* 时间线图示 */}
              <div className={styles.timelineSection}>
                <div className={styles.timelineTitle}>项目时间线</div>
                <div className={styles.timeline}>
                  <div className={styles.timelineItem}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineDate}>今天</div>
                      <div className={styles.timelineLabel}>任务发布</div>
                    </div>
                  </div>
                  <div className={styles.timelineConnector}>
                    <span className={styles.timelineDuration}>{biddingDays}天 · 投递期</span>
                  </div>
                  <div className={styles.timelineItem}>
                    <div className={`${styles.timelineDot} ${styles.timelineDotActive}`} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineDate}>{formatDateCN(formData.biddingDeadline)}</div>
                      <div className={styles.timelineLabel}>投递截止</div>
                    </div>
                  </div>
                  <div className={styles.timelineConnector}>
                    <span className={styles.timelineDuration}>{deliverDays}天 · 交付期</span>
                  </div>
                  <div className={styles.timelineItem}>
                    <div className={`${styles.timelineDot} ${styles.timelineDotEnd}`} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineDate}>{formatDateCN(formData.completionDeadline)}</div>
                      <div className={styles.timelineLabel}>项目交付</div>
                    </div>
                  </div>
                </div>
                <div className={styles.timelineSummary}>
                  项目总周期：{totalDays}天
                </div>
              </div>
            </div>

            <div className={styles.noticeCard}>
              <AlertCircle className={styles.noticeIcon} />
              <div className={styles.noticeContent}>
                <p>发布任务后：</p>
                <ul>
                  <li>赏金将被冻结在托管账户</li>
                  <li>任务进入投递期，工程师可以投递方案</li>
                  <li>您可以在"我发布的"中管理任务</li>
                </ul>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <button onClick={() => navigate(-1)} className={styles.backBtn}>
          <ArrowLeft className={styles.backIcon} />
        </button>
        <h1>发布任务</h1>
      </header>

      {/* Stepper */}
      <div className={styles.stepper}>
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`${styles.step} ${index === currentStep ? styles.active : ""} ${
              index < currentStep ? styles.completed : ""
            }`}
          >
            <div className={styles.stepNumber}>{index < currentStep ? "✓" : index + 1}</div>
            <span className={styles.stepLabel}>{step.label}</span>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className={styles.content}>{renderStepContent()}</div>

      {/* Footer Buttons */}
      <div className={styles.footer}>
        {currentStep > 0 && (
          <button onClick={handlePrev} className={styles.btnSecondary} disabled={isSubmitting}>
            上一步
          </button>
        )}
        {currentStep < steps.length - 1 ? (
          <button onClick={handleNext} className={styles.btnPrimary}>
            下一步
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className={styles.btnPrimary}
            disabled={isSubmitting}
          >
            {isSubmitting ? "发布中..." : "确认发布"}
          </button>
        )}
      </div>
    </div>
  );
}
